import { useHistory, useRouteMatch } from '@react-router';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useEffect } from 'react';
import { CATALOG_PRODUCT_LIST_PAGE_SIZE } from '../../Catalog.constants';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useCatalogProductListData } from '../../hooks/useCatalogProductListData/useCatalogProductListData';
import { useInitialPageSizeFromLocationState } from '../../hooks/useTrackCatalogPageViaLocationState';
import { setCurationNavigationState, useCatalogContext } from '../../state/Catalog';
import { CatalogDepartmentEntry } from '../../state/Catalog/Catalog.types';
import { useDeriveTelemetryPage } from '../../utils/deriveTelemetryPage';
import { VALUE_PROP_CARDS_PER_PAGE } from '../../components/ProductTile/components/ValuePropCard/constants';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useAuth } from '@shared/components/AuthProvider';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import { UniversalCatalogSearchMode, useUniversalSearchFeature } from '../../hooks/useUniversalSearchFeature';

const useCatalogProductsIndexPageTelemetry = ({ isReadyToTrack }: { isReadyToTrack: boolean }) => {
  const {
    selectedCategory,
    getProductCatalogCategoryBasePageEventProps,
    root: trackRootPage,
    department: trackDepartmentPage,
    category: trackCategoryPage,
    subcategory: trackSubcategoryPage,
  } = useCatalogLoggingContext();

  const trackPage = useEventCallback(
    (action: 'enter') => {
      const categoryPageProps = getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs;
      if (!selectedCategory) {
        trackRootPage[action](categoryPageProps);
      } else {
        switch (selectedCategory.lineageCsp?.length) {
          case 1:
            trackDepartmentPage[action](categoryPageProps);
            break;
          case 2:
            trackCategoryPage[action](categoryPageProps);
            break;
          case 3:
            trackSubcategoryPage[action](categoryPageProps);
            break;
          default:
            break;
        }
      }
    },
    [selectedCategory],
  );

  useEffect(() => {
    if (!isReadyToTrack) {
      return () => {};
    }

    trackPage('enter');
    return () => {};
  }, [isReadyToTrack, trackPage]);
};

export const useShopRootController = () => {
  useCatalogProductsIndexPageTelemetry({ isReadyToTrack: true });
};

export interface CatalogProductIndexControllerProps {
  categoryLocator: Maybe<string>;
  searchTerm: Maybe<string>;
}

export const useCatalogProductsIndexController = (props: CatalogProductIndexControllerProps) => {
  const { categoryLocator, searchTerm } = props;

  const history = useHistory();

  const { config } = useShopStorefrontContext();
  const { universalCatalogSearchMode, loading: loadingUniversalSearch } = useUniversalSearchFeature();
  const { isCurrentUserSettled, isLoggedIn } = useAuth();

  const { catalogRoutes, isBabyStorefront } = useCatalogRoutePaths();

  const isRecommendedProductsPage = useRouteMatch({ path: catalogRoutes.recommendedProducts.paths });

  const { eventId, isMobile, addCatalogItemToRegistry, addCatalogItemLoading, selectedCategory, redirectPath } = useCatalogContext();

  const { catalogProductsIndexQueryError } = useCatalogLoggingContext();

  const isMobileOrTablet = useIsMobileOrTablet();
  const deriveTelemetryPage = useDeriveTelemetryPage();
  const gridTelemetryPageContext = deriveTelemetryPage({ view: 'productGrid', product: null, selectedCategory });
  const showValuePropCards = !searchTerm;

  const pageSize = showValuePropCards ? CATALOG_PRODUCT_LIST_PAGE_SIZE - VALUE_PROP_CARDS_PER_PAGE : CATALOG_PRODUCT_LIST_PAGE_SIZE;

  const initialPageSize = useInitialPageSizeFromLocationState(pageSize, !!showValuePropCards);
  const {
    data,
    loading,
    productList,
    totalProductCount,
    filters,
    sortBy,
    error,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage,
    isFetchingMore,
    loadFallbackCatalogProducts,
    fallbackCatalogProductList,
    fallbackCatalogDataError,
    loadingFallbackCatalog,
  } = useCatalogProductListData({
    url: isRecommendedProductsPage ? (isBabyStorefront ? '/shop/baby' : '/shop') : undefined,
    pageSize,
    selectedCategory,
    searchTerm,
    telemetryPageValue: gridTelemetryPageContext,
    showValuePropCards: showValuePropCards,
    initialPageSize,
    shouldApplyFilters: true,
  });
  const shouldRevealGrid = !!data && !(loading && !productList.length);
  const showUniversalSearch =
    !loadingUniversalSearch &&
    shouldRevealGrid &&
    universalCatalogSearchMode !== UniversalCatalogSearchMode.disabled &&
    (universalCatalogSearchMode === UniversalCatalogSearchMode.separatedProductGrid || productList.length === 0);

  useEffect(() => {
    if (
      !!searchTerm?.length &&
      !showUniversalSearch &&
      !loading &&
      !loadingFallbackCatalog &&
      (!productList || productList.length <= 0) &&
      fallbackCatalogProductList.length === 0
    ) {
      loadFallbackCatalogProducts();
    }
  }, [productList, loadFallbackCatalogProducts, searchTerm?.length, loading, loadingFallbackCatalog, fallbackCatalogProductList, showUniversalSearch]);

  useEffect(() => {
    const apolloError = error || fallbackCatalogDataError;
    if (apolloError) {
      catalogProductsIndexQueryError({ error: apolloError, categoryLocator });
    }
  }, [error, categoryLocator, catalogProductsIndexQueryError, fallbackCatalogDataError]);

  useEffect(() => {
    setCurationNavigationState(null);
  }, []);

  useCatalogProductsIndexPageTelemetry({ isReadyToTrack: shouldRevealGrid });

  const selectedCategoryChildren = (selectedCategory as CatalogDepartmentEntry)?.children;
  const showTopCategoryShowcase = selectedCategoryChildren && selectedCategoryChildren.length > 1;

  /**
   * Intended to be used for universal search interaction
   */
  const verifyUserAccessAndInvokeCb = useEventCallback((cb: () => void) => {
    if (isCurrentUserSettled && !isLoggedIn) {
      history.push(config.createEventPathname);
      return;
    }

    cb();
  });

  return {
    catalogSearchResults: productList,
    fallbackCatalogProductList,
    filters,
    sortBy,
    eventId,
    isMobile,
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    gridTelemetryPageContext,
    selectedCategory,
    isLoadingPrimaryCatalog: loading,
    loading: !!loading || !!loadingFallbackCatalog || !!loadingUniversalSearch,
    showSkeleton: !shouldRevealGrid || !!error || !!fallbackCatalogDataError || !!loadingFallbackCatalog,
    shouldRevealGrid,
    isMobileOrTablet,
    searchTerm,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage,
    isFetchingMore,
    totalProductCount,
    isRecommendedProductsPage,
    showTopCategoryShowcase,
    verifyUserAccessAndInvokeCb,
    redirectPath,
    showUniversalSearch,
  };
};
